import React, { useEffect, useState, useRef } from "react";
import Navbar from "../Navbar/Navbar";
import SideMenu from "./SideMenu";
import { PreOrdersListResponse } from "@/api/PreOrdersAPI";
import { toast } from "../ui/use-toast";
import { PreOrderModel } from "@/api/Models";
import ApiHttpClient from "@/HttpClient";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {useCheckUserUM} from "@/components/utils/useCheckUserUM";
import {getDecodedTokenDetails} from "@/components/Token/TokenService";

const SearchInput: React.FC<any> = ({ searchPreOrder, setSearchPreOrder, setIsNewSearch, searchInputText }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length > 1) {
            setSearchPreOrder(e.target.value);
            setIsNewSearch(true);
        } else {
            setSearchPreOrder(e.target.value);
        }
    };

    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            value={searchPreOrder}
            onChange={handleChange}
            placeholder={searchInputText}
        />
    );
};

function MyPreOrdersInfo() {
    const [currentPage, setCurrentPage] = useState(0);
    const [searchPreOrder, setSearchPreOrder] = useState("");
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [data, setData] = useState<PreOrdersListResponse>();
    const [userUM, setUserUM] = useState<boolean | null>(null);
    const [dateMin, setDateMin] = useState<any>();
    const [dateMax, setDateMax] = useState<any>(new Date());
    const [preOrderType, setPreOrderType] = useState("");
    const [userRole, setUserRole] = useState("");
    const [searchInputText, setSearchInputText] = useState("");
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const pageSize = 30;

    const detailsRef = useRef<HTMLDetailsElement>(null);

    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();

            if (tokenDetails?.token) {
                try {
                    const userRole = tokenDetails.userRole
                    setUserRole(userRole);

                    if(userRole !== "BASIC")
                        setSearchInputText("Cauta codul unei precomenzi sau numele unei organizatii...")
                    else setSearchInputText("Caută codul unei precomenzi...")
                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);

    useCheckUserUM((value: boolean) => setUserUM(value));
    
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.removeAttribute("open");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (data?.totalPages && value > data.totalPages) {
            value = data.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (data?.totalPages && value >= 1 && value <= data?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };

    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
        setIsNewSearch(false);
    };

    const handleRemoveFilters = () => {
        setCurrentPage(0);
        setSearchPreOrder('');
        setIsNewSearch(false);
        setDateMin('');
        setDateMax(new Date());
        setPreOrderType('');
    }

    useEffect(() => {
        const updatePreOrders = async () => {
            setIsLoadingSearch(true);

            let response;
            const filters = [];

            if (dateMin) {
                filters.push({
                    field: "orderDate",
                    operator: "gte",
                    value: new Date(dateMin).toISOString()
                });
            }

            if (dateMax) {
                filters.push({
                    field: "orderDate",
                    operator: "lte",
                    value: new Date(dateMax).toISOString()
                });
            }

            if (preOrderType === "TRP") {
                filters.push({
                    field: "documentType.documentTypeCode",
                    operator: "eq",
                    value: "TRP"
                });
            }

            if (preOrderType === "POD") {
                filters.push({
                    field: "documentType.documentTypeCode",
                    operator: "eq",
                    value: "POD"
                });
            }

            if (preOrderType === "RPO") {
                filters.push({
                    field: "documentType.documentTypeCode",
                    operator: "eq",
                    value: "RPO"
                });
            }

            const requestBody = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                orderBy: ["id Desc"],
                pageNumber: currentPage + 1,
                pageSize: pageSize,
            }

            const requestBodySearchAndDate = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                advancedSearch: {
                    fields: [
                        "id",
                        "workingPoint.organization.name",
                        "workingPointId"
                    ],
                    keyword: searchPreOrder
                },
                orderBy: ["id Desc"],
                pageNumber: currentPage + 1,
                pageSize: pageSize,
            }

            if (searchPreOrder) {
                if (isNewSearch) setCurrentPage(0);

                if ((dateMin && dateMax) || preOrderType) {
                    response = await ApiHttpClient().post<PreOrdersListResponse>(`/preOrder/search`, requestBodySearchAndDate);
                } else {
                    response = await ApiHttpClient().post<PreOrdersListResponse>('/preOrder/search', {
                        "advancedSearch": {
                            "fields": [
                                "id",
                                "workingPoint.organization.name",
                                "workingPointId"
                            ],
                            "keyword": searchPreOrder
                        },
                        "orderBy": ["id Desc"],
                        "pageNumber": currentPage + 1,
                        "pageSize": pageSize,
                    });
                }
            } else if ((dateMin && dateMax) || preOrderType) {
                response = await ApiHttpClient().post<PreOrdersListResponse>(`/preOrder/search`, requestBody);

            } else {
                response = await ApiHttpClient().post<PreOrdersListResponse>(`/preOrder/search`, {
                    "keyword": "",
                    "pageNumber": currentPage + 1,
                    "pageSize": pageSize,
                    "orderBy": ["id Desc"]
                });
            }
            if (!response?.data) {
                toast({ description: "A aparut o eroare pentru afisarea precomenzilor" });
                return <></>;
            }
            setIsLoadingSearch(false);
            setData(response.data);
        }

        updatePreOrders();
    }, [searchPreOrder, currentPage, pageSize, isNewSearch, dateMin, dateMax, preOrderType])

    return (
        <>
            <Navbar />
            <div className="flex flex-row items-start">
                <SideMenu />
                {/* Desktop Version */}
                <div className="hidden mx-auto md:block z-auto">
                    {!isLoadingSearch && data?.totalPages === 0 && <p className="mt-5 ml-5 mb-3">Nu au fost gasite rezultate.</p>}
                    <div className="relative overflow-x-auto mx-8 shadow-md sm:rounded-lg overflow-y-auto pt-4 pb-16">

                        <div className="flex justify-end mb-2 mr-2">
                            <button
                                className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                                onClick={handleRemoveFilters}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    className="size-3"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>

                                <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                            </button>
                        </div>

                        <div className="mb-1">
                            <SearchInput searchPreOrder={searchPreOrder} setSearchPreOrder={setSearchPreOrder}
                                setIsNewSearch={setIsNewSearch} searchInputText={searchInputText} />
                        </div>

                        <div className="flex flex-col ml-1 mb-1">
                            <select id="underline_select"
                                    value={preOrderType ?? ""}
                                    onChange={(e) => {
                                        setPreOrderType(e.target.value)
                                    }
                                    }
                                    className="block py-2.5 my-2 px-0 mx-2 w-40 text-sm text-gray-500 bg-transparent border-0 border-b-2 border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer">
                                <option value="">Tipul precomenzii</option>
                                <option value={"POD"}>(POD) Precomanda Stoc</option>
                                <option value={"TRP"}>(TRP) Precomanda Tranzit</option>
                                <option value={"RPO"}>(TRP) Precomanda Retur</option>
                            </select>
                        </div>
                        <details ref={detailsRef} className="group relative mb-5">
                            <summary
                                className="mx-2 flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                            >
                                <span className="text-sm font-medium"> Cauta precomenzile din </span>

                                <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                </span>
                            </summary>

                            <div
                                className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                            >
                                <div className="flex items-center justify-between px-3 py-2">
                                    <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDateMin('');
                                            setDateMax(new Date())
                                        }}
                                        className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                    >
                                        Reset
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 p-3">
                                    <label htmlFor="MinPrice">
                                        <span className="text-sm text-gray-700"> Data de inceput </span>

                                        <DatePicker
                                            selected={dateMin}
                                            onChange={(dateMin) => setDateMin(dateMin)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: { boundary: "viewport", tether: false },
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: { fallbackPlacements: [] }, // Prevents flipping above
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                        />
                                    </label>

                                    <label htmlFor="MaxPrice">
                                        <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                        <DatePicker
                                            selected={dateMax}
                                            onChange={(dateMax) => setDateMax(dateMax)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: { boundary: "viewport", tether: false },
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: { fallbackPlacements: [] },
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal"
                                        />
                                    </label>
                                </div>
                            </div>
                        </details>

                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead
                                className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">
                                        Numarul precomenzii
                                    </th>
                                    {(userRole !== "BASIC") && <th scope="col" className="px-6 py-3">
                                        Tipul precomenzii
                                    </th>}
                                    {(userRole !== "BASIC") && <th scope="col" className="px-6 py-3">
                                        Numele organizatiei
                                    </th>}
                                    <th scope="col" className="px-6 py-3">
                                        Data plasarii precomenzii
                                    </th>
                                    <th scope="col" className="px-6 py-3">
                                        Ora plasarii precomenzii
                                    </th>
                                    {!userUM && <th scope="col" className="px-6 py-3">
                                        Data de livrare estimata
                                    </th>}
                                    <th scope="col" className="px-6 py-3">
                                        Status precomanda
                                    </th>
                                    <th scope="col" className="px-6 py-3">
                                        Valoare precomanda
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.items.map((preOrder: PreOrderModel) => {
                                    let orderDate = new Date(preOrder.orderDate);
                                    orderDate.setHours(orderDate.getHours());
                                    let hours = String(orderDate.getHours()).padStart(2, '0');
                                    let minutes = String(orderDate.getMinutes()).padStart(2, '0');
                                    let seconds = String(orderDate.getSeconds()).padStart(2, '0');
                                    let timeString = `${hours}:${minutes}:${seconds}`;
                                    return (
                                        <tr className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700">
                                            <th scope="row"
                                                className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                <a href={"/my-account/preorders/pre-order-detail/" + preOrder.id}
                                                    className="text-blue-500 underline z-auto">{preOrder.id}</a>
                                            </th>
                                            {(userRole !== "BASIC") && <td className="px-6 py-4">
                                                {preOrder.documentType.documentTypeCode}
                                            </td>}
                                            {(userRole !== "BASIC") && <td className="px-6 py-4">
                                                {preOrder.workingPoint.organization.name}
                                            </td>}
                                            <td className="px-6 py-4">
                                                {preOrder.orderDate.split("T")[0]}
                                            </td>
                                            <td className="px-6 py-4">
                                                {/* {preOrder.orderDate.split("T")[1].split(".")[0]} */}
                                                {timeString}
                                            </td>
                                            {!userUM && <td className="px-6 py-4">
                                                {preOrder.expectedDeliveryDate.split("T")[0]}
                                            </td>}
                                            <td className="px-6 py-4">
                                                {preOrder.documentStatus.documentStatusName}
                                            </td>
                                            <td className="px-6 py-4">
                                                {preOrder.totalValue} {preOrder.currency}
                                            </td>
                                        </tr>
                                    );
                                })}

                            </tbody>
                        </table>
                    </div>

                    <div className="text-center mt-8 mb-2">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (data && currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={data && currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Mobile Version */}
                <div className="mx-auto max-w-screen-xl md:hidden z-0">
                    {!isLoadingSearch && data?.totalPages === 0 && <p>Nu au fost gasite rezultate.</p>}

                    <div className="flex justify-end mt-4 mr-2">
                        <button
                            className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                            onClick={handleRemoveFilters}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                className="size-3"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>

                            <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                        </button>
                    </div>

                    <div className="-my-3 py-3 ml-12 mt-2 mb-8 mr-12">
                        <SearchInput searchPreOrder={searchPreOrder} setSearchPreOrder={setSearchPreOrder}
                                     setIsNewSearch={setIsNewSearch} searchInputText={searchInputText}/>
                    </div>

                    <div className="flex flex-col ml-12">
                        <select id="underline_select"
                                value={preOrderType ?? ""}
                                onChange={(e) => {
                                    setPreOrderType(e.target.value)
                                }
                                }
                                className="block py-2.5 my-2 px-0 mx-2 w-40 text-sm text-gray-500 bg-transparent border-0 border-b-2 border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer">
                            <option value="">Tipul precomenzii</option>
                            <option value={"POD"}>(POD) Precomanda Stoc</option>
                            <option value={"TRP"}>(TRP) Precomanda Tranzit</option>
                            <option value={"RPO"}>(TRP) Precomanda Retur</option>
                        </select>
                    </div>

                    <details className="group relative ml-12">
                        <summary
                            className="flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                        >
                            <span className="text-sm font-medium"> Cauta comenzile din </span>

                            <span className="transition-transform group-open:-rotate-180">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="size-4"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                          d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                </svg>
                            </span>
                        </summary>

                        <div
                            className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                        >
                            <div className="flex items-center justify-between px-3 py-2">
                                <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setDateMin('');
                                        setDateMax(new Date())
                                    }}
                                    className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="flex items-center gap-3 p-3">
                                <label htmlFor="MinPrice">
                                    <span className="text-sm text-gray-700"> Data de inceput </span>

                                    <DatePicker
                                        selected={dateMin}
                                        onChange={(dateMin) => setDateMin(dateMin)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: { boundary: "viewport", tether: false },
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: { fallbackPlacements: [] }, // Prevents flipping above
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                    />
                                </label>

                                <label htmlFor="MaxPrice">
                                    <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                    <DatePicker
                                        selected={dateMax}
                                        onChange={(dateMax) => setDateMax(dateMax)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: { boundary: "viewport", tether: false },
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: { fallbackPlacements: [] },
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal"
                                    />
                                </label>
                            </div>
                        </div>
                    </details>

                    {data?.items.map((preOrder: PreOrderModel) => {
                        let orderDate = new Date(preOrder.orderDate);
                        orderDate.setHours(orderDate.getHours());
                        let hours = String(orderDate.getHours()).padStart(2, '0');
                        let minutes = String(orderDate.getMinutes()).padStart(2, '0');
                        let seconds = String(orderDate.getSeconds()).padStart(2, '0');
                        let timeString = `${hours}:${minutes}:${seconds}`;
                        return (
                            <div
                                className="flow-root rounded-lg border border-gray-100 py-3 ml-12 mt-8 mb-8 mr-12 shadow-sm">
                                <dl className="-my-3 divide-y divide-gray-100 text-sm">
                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Numarul precomenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2"><a
                                            href={"/my-account/preorders/pre-order-detail/" + preOrder.id}
                                            className="text-blue-500 underline">{preOrder.id}</a></dd>
                                    </div>

                                    {(userRole !== "BASIC") && <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Tipul precomenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.documentType.documentTypeCode}</dd>
                                    </div>}

                                    {(userRole !== "BASIC") && <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Numele organizatiei</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.workingPoint.organization.name}</dd>
                                    </div>}

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data plasarii precomenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.orderDate.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Ora plasarii precomenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{timeString}</dd>
                                    </div>

                                    {!userUM && <div
                                        className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data de livrare estimata</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.expectedDeliveryDate.split("T")[0]}</dd>
                                    </div>}

                                    {!userUM && <div
                                        className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data reala de livrare</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.shipping ? preOrder.shipping.deliveryDate.split("T")[0] : '-'}</dd>
                                    </div>}

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Status precomanda</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.documentStatus.documentStatusName}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Valoare precomanda</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{preOrder.totalValue} {preOrder.currency}</dd>
                                    </div>
                                </dl>
                            </div>
                        );
                    })}

                    <div className="text-center mb-4">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (data && currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={data && currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
}

export default MyPreOrdersInfo;